import { env } from '@/utils/constants/env';

export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface KeycloakUserInfo {
  sub: string;
  email_verified: boolean;
  name: string;
  preferred_username: string;
  given_name: string;
  family_name: string;
  email: string;
}

export class KeycloakAuthService {
  private static instance: KeycloakAuthService;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): KeycloakAuthService {
    if (!KeycloakAuthService.instance) {
      KeycloakAuthService.instance = new KeycloakAuthService();
    }
    return KeycloakAuthService.instance;
  }

  /**
   * Generate authorization URL for Keycloak with PKCE
   */
  public generateAuthUrl(codeVerifier: string, codeChallenge: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: env.keycloakClientId,
      redirect_uri: env.keycloakRedirectUri,
      scope: 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: Math.random().toString(36).substring(2, 15),
    });

    return `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/auth?${params}`;
  }

  /**
   * Exchange authorization code for tokens using client secret
   */
  public async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<KeycloakTokenResponse> {
    const tokenEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.keycloakClientId,
      client_secret: env.keycloakClientSecret || '',
      code,
      redirect_uri: env.keycloakRedirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${errorData}`);
    }

    return response.json();
  }

  /**
   * Refresh access token using refresh token
   */
  public async refreshAccessToken(refreshToken: string): Promise<KeycloakTokenResponse> {
    const tokenEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.keycloakClientId,
      client_secret: env.keycloakClientSecret || '',
      refresh_token: refreshToken,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Token refresh failed: ${response.status} - ${errorData}`);
    }

    return response.json();
  }

  /**
   * Get user information from Keycloak
   */
  public async getUserInfo(accessToken: string): Promise<KeycloakUserInfo> {
    const userInfoEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/userinfo`;

    const response = await fetch(userInfoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to get user info: ${response.status} - ${errorData}`);
    }

    return response.json();
  }

  /**
   * Logout from Keycloak
   */
  public async logout(refreshToken: string, redirectUri?: string): Promise<void> {
    const logoutEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/logout`;

    const body = new URLSearchParams({
      client_id: env.keycloakClientId,
      client_secret: env.keycloakClientSecret || '',
      refresh_token: refreshToken,
    });

    if (redirectUri) {
      body.append('post_logout_redirect_uri', redirectUri);
    }

    const response = await fetch(logoutEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      console.warn('Logout request failed, but continuing with local cleanup');
    }
  }

  /**
   * Validate and decode JWT token
   */
  public decodeToken(token: string): any {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw error;
    }
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      const exp = payload.exp;

      if (typeof exp !== 'number') {
        return true;
      }

      // Add a small buffer (30 seconds) to prevent edge cases
      return exp * 1000 < Date.now() + 30000;
    } catch (error) {
      return true;
    }
  }
}

export const keycloakAuth = KeycloakAuthService.getInstance();
